import * as z from 'zod';

export const CreateConnectedDomainRequest = z.object({
  connectedDomain: z
    .object({
      _id: z
        .string()
        .describe(
          'ID of the connected domain. Identical to the domain name including TLD.'
        )
        .optional(),
      domain: z
        .string()
        .describe(
          'Domain name including TLD.\nBoth root domains and subdomains are supported.'
        ),
      connectionType: z.enum(['POINTING', 'NAMESERVERS', 'HIDDEN']).optional(),
      siteInfo: z
        .intersection(
          z.object({
            _id: z
              .string()
              .describe('ID of the site to which the domain is assigned.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            assignmentType: z.enum(['PRIMARY', 'REDIRECT']).optional(),
          }),
          z.xor([
            z.object({ redirectInfo: z.never().optional() }),
            z.object({
              redirectInfo: z
                .object({
                  primaryDomain: z
                    .string()
                    .describe(
                      'Primary domain to which this domain will redirect.'
                    )
                    .optional(),
                })
                .describe(
                  'Redirect information, relevant when `assignmentType` = `REDIRECT`.'
                ),
            }),
          ])
        )
        .describe(
          "Information about the site to which the domain is assigned, and whether\nit's the primary domain or a redirect."
        )
        .optional(),
      suppressNotifications: z
        .boolean()
        .describe(
          'Whether the site owner receives standard email notifications from Wix about\nthe connected domain.\n\nDefault: `false`'
        )
        .optional(),
      dnsPropagationStatus: z
        .enum(['COMPLETED', 'FAILED', 'IN_PROGRESS'])
        .optional(),
      _createdDate: z
        .date()
        .describe(
          'Date and time the `connectedDomain` object was created in\n`YYYY-MM-DDThh:mm:ss.sssZ` format.'
        )
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe(
          'Date and time the `connectedDomain` object was last updated in\n`YYYY-MM-DDThh:mm:ss.sssZ` format.'
        )
        .optional()
        .nullable(),
    })
    .describe('Domain to connect.'),
});
export const CreateConnectedDomainResponse = z.object({
  _id: z
    .string()
    .describe(
      'ID of the connected domain. Identical to the domain name including TLD.'
    )
    .optional(),
  domain: z
    .string()
    .describe(
      'Domain name including TLD.\nBoth root domains and subdomains are supported.'
    )
    .optional(),
  connectionType: z
    .enum(['POINTING', 'NAMESERVERS', 'HIDDEN'])
    .describe('How the domain is connected to the Wix site.')
    .optional(),
  siteInfo: z
    .intersection(
      z.object({
        _id: z
          .string()
          .describe('ID of the site to which the domain is assigned.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        assignmentType: z
          .enum(['PRIMARY', 'REDIRECT'])
          .describe(
            'The relationship assigned for this domain to the site.\n\nRead more about [primary and redirected domains](https://support.wix.com/en/article/switching-your-primary-and-redirected-domains).'
          )
          .optional(),
      }),
      z.xor([
        z.object({ redirectInfo: z.never().optional() }),
        z.object({
          redirectInfo: z
            .object({
              primaryDomain: z
                .string()
                .describe('Primary domain to which this domain will redirect.')
                .optional(),
            })
            .describe(
              'Redirect information, relevant when `assignmentType` = `REDIRECT`.'
            ),
        }),
      ])
    )
    .describe(
      "Information about the site to which the domain is assigned, and whether\nit's the primary domain or a redirect."
    )
    .optional(),
  suppressNotifications: z
    .boolean()
    .describe(
      'Whether the site owner receives standard email notifications from Wix about\nthe connected domain.\n\nDefault: `false`'
    )
    .optional(),
  dnsPropagationStatus: z
    .enum(['COMPLETED', 'FAILED', 'IN_PROGRESS'])
    .describe('DNS propagation status.')
    .optional(),
  _createdDate: z
    .date()
    .describe(
      'Date and time the `connectedDomain` object was created in\n`YYYY-MM-DDThh:mm:ss.sssZ` format.'
    )
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe(
      'Date and time the `connectedDomain` object was last updated in\n`YYYY-MM-DDThh:mm:ss.sssZ` format.'
    )
    .optional()
    .nullable(),
});
export const GetConnectedDomainRequest = z.object({
  connectedDomainId: z
    .string()
    .describe(
      'ID of the connected domain. Identical to the domain name including TLD.'
    ),
});
export const GetConnectedDomainResponse = z.object({
  _id: z
    .string()
    .describe(
      'ID of the connected domain. Identical to the domain name including TLD.'
    )
    .optional(),
  domain: z
    .string()
    .describe(
      'Domain name including TLD.\nBoth root domains and subdomains are supported.'
    )
    .optional(),
  connectionType: z
    .enum(['POINTING', 'NAMESERVERS', 'HIDDEN'])
    .describe('How the domain is connected to the Wix site.')
    .optional(),
  siteInfo: z
    .intersection(
      z.object({
        _id: z
          .string()
          .describe('ID of the site to which the domain is assigned.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        assignmentType: z
          .enum(['PRIMARY', 'REDIRECT'])
          .describe(
            'The relationship assigned for this domain to the site.\n\nRead more about [primary and redirected domains](https://support.wix.com/en/article/switching-your-primary-and-redirected-domains).'
          )
          .optional(),
      }),
      z.xor([
        z.object({ redirectInfo: z.never().optional() }),
        z.object({
          redirectInfo: z
            .object({
              primaryDomain: z
                .string()
                .describe('Primary domain to which this domain will redirect.')
                .optional(),
            })
            .describe(
              'Redirect information, relevant when `assignmentType` = `REDIRECT`.'
            ),
        }),
      ])
    )
    .describe(
      "Information about the site to which the domain is assigned, and whether\nit's the primary domain or a redirect."
    )
    .optional(),
  suppressNotifications: z
    .boolean()
    .describe(
      'Whether the site owner receives standard email notifications from Wix about\nthe connected domain.\n\nDefault: `false`'
    )
    .optional(),
  dnsPropagationStatus: z
    .enum(['COMPLETED', 'FAILED', 'IN_PROGRESS'])
    .describe('DNS propagation status.')
    .optional(),
  _createdDate: z
    .date()
    .describe(
      'Date and time the `connectedDomain` object was created in\n`YYYY-MM-DDThh:mm:ss.sssZ` format.'
    )
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe(
      'Date and time the `connectedDomain` object was last updated in\n`YYYY-MM-DDThh:mm:ss.sssZ` format.'
    )
    .optional()
    .nullable(),
});
export const DeleteConnectedDomainRequest = z.object({
  connectedDomainId: z
    .string()
    .describe(
      'ID of the connected domain to delete. Identical to the domain name including TLD.'
    ),
});
export const DeleteConnectedDomainResponse = z.object({});
export const ListConnectedDomainsRequest = z.object({
  options: z
    .object({
      paging: z
        .object({
          limit: z
            .number()
            .int()
            .describe('Number of domains to load.\n\nMin: `0`\nMax: `100`')
            .min(0)
            .max(100)
            .optional()
            .nullable(),
          cursor: z
            .string()
            .describe(
              "Pointer to the next or previous page in the list of results.\n\nYou can get the relevant cursor token\nfrom the `pagingMetadata` object in the previous call's response.\nNot relevant for the first request."
            )
            .optional()
            .nullable(),
        })
        .describe('Cursor paging options.')
        .optional(),
    })
    .describe('Filter options.')
    .optional(),
});
export const ListConnectedDomainsResponse = z.object({
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of domains returned in the response.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe('Cursor pointing to next page in the list of results.')
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to previous page in the list of results.'
            )
            .optional()
            .nullable(),
        })
        .describe(
          'Cursors to navigate through the result pages using `next` and `prev`. Returned if cursor paging is used.'
        )
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Indicates if there are more results after the current page.\nIf `true`, another page of results can be retrieved.\nIf `false`, this is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('Metadata about the returned list of connected domains.')
    .optional(),
  connectedDomains: z
    .array(
      z.object({
        _id: z
          .string()
          .describe(
            'ID of the connected domain. Identical to the domain name including TLD.'
          )
          .optional(),
        domain: z
          .string()
          .describe(
            'Domain name including TLD.\nBoth root domains and subdomains are supported.'
          )
          .optional(),
        connectionType: z
          .enum(['POINTING', 'NAMESERVERS', 'HIDDEN'])
          .describe('How the domain is connected to the Wix site.')
          .optional(),
        siteInfo: z
          .intersection(
            z.object({
              _id: z
                .string()
                .describe('ID of the site to which the domain is assigned.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              assignmentType: z
                .enum(['PRIMARY', 'REDIRECT'])
                .describe(
                  'The relationship assigned for this domain to the site.\n\nRead more about [primary and redirected domains](https://support.wix.com/en/article/switching-your-primary-and-redirected-domains).'
                )
                .optional(),
            }),
            z.xor([
              z.object({ redirectInfo: z.never().optional() }),
              z.object({
                redirectInfo: z
                  .object({
                    primaryDomain: z
                      .string()
                      .describe(
                        'Primary domain to which this domain will redirect.'
                      )
                      .optional(),
                  })
                  .describe(
                    'Redirect information, relevant when `assignmentType` = `REDIRECT`.'
                  ),
              }),
            ])
          )
          .describe(
            "Information about the site to which the domain is assigned, and whether\nit's the primary domain or a redirect."
          )
          .optional(),
        suppressNotifications: z
          .boolean()
          .describe(
            'Whether the site owner receives standard email notifications from Wix about\nthe connected domain.\n\nDefault: `false`'
          )
          .optional(),
        dnsPropagationStatus: z
          .enum(['COMPLETED', 'FAILED', 'IN_PROGRESS'])
          .describe('DNS propagation status.')
          .optional(),
        _createdDate: z
          .date()
          .describe(
            'Date and time the `connectedDomain` object was created in\n`YYYY-MM-DDThh:mm:ss.sssZ` format.'
          )
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe(
            'Date and time the `connectedDomain` object was last updated in\n`YYYY-MM-DDThh:mm:ss.sssZ` format.'
          )
          .optional()
          .nullable(),
      })
    )
    .optional(),
});
